import React, { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { useUserDataContext } from "../../context/UserDataContext";
import { billingApi } from "../../services/api";

enum BillingFrequency {
  Monthly = "monthly",
  Annual = "annual",
}

interface CardEntryFormProps {
  selectedPlan: number | null;
  billingFrequency: BillingFrequency;
  onPaymentSuccess: () => void;
  seatCount?: number;
}

const CardEntryForm: React.FC<CardEntryFormProps> = ({
  selectedPlan,
  billingFrequency,
  onPaymentSuccess,
  seatCount: seatCountProp,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { firmData, refreshUserData } = useUserDataContext();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const seatCount = seatCountProp || firmData?.users?.length || 1;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsProcessing(true);
    setError(null);

    if (!stripe || !elements) {
      setIsProcessing(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setIsProcessing(false);
      return;
    }

    const { paymentMethod, error: stripeError } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
    });

    if (stripeError) {
      setError(stripeError.message || "Payment method creation failed.");
      setIsProcessing(false);
      return;
    }

    try {
      await billingApi.charge({
        paymentMethodId: paymentMethod.id,
        plan: selectedPlan!,
        duration: billingFrequency,
        seatCount,
      });
      onPaymentSuccess();
      refreshUserData();
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Payment failed. Please try again.";
      setError(msg);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Card input */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Card Information
        </label>
        <div className="border border-slate-200 rounded-xl px-4 py-3.5 bg-white focus-within:ring-2 focus-within:ring-emerald-500/20 focus-within:border-emerald-500 transition-all">
          <CardElement
            options={{
              style: {
                base: {
                  iconColor: "#64748b",
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  fontSmoothing: "antialiased",
                  fontSize: "16px",
                  color: "#1e293b",
                  "::placeholder": {
                    color: "#94a3b8",
                  },
                },
                invalid: {
                  color: "#ef4444",
                  iconColor: "#ef4444",
                },
              },
            }}
          />
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm rounded-xl px-4 py-3">
          <span>{error}</span>
        </div>
      )}

      {/* Submit button */}
      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full py-3.5 bg-brand text-white rounded-xl font-bold text-sm hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isProcessing ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Processing...
          </span>
        ) : (
          "Subscribe Now"
        )}
      </button>

      {/* Security & legal footer */}
      <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
        <Lock className="h-3 w-3" />
        <span>Secured by Stripe. 256-bit SSL encryption.</span>
      </div>
      <p className="text-xs text-slate-400 text-center leading-relaxed">
        Cancel anytime from your dashboard. By subscribing you agree to the{" "}
        <a href="#" className="underline hover:text-slate-600">Terms of Service</a>
        {" "}and{" "}
        <a href="#" className="underline hover:text-slate-600">Privacy Policy</a>.
      </p>
    </form>
  );
};

export default CardEntryForm;
